/**
 * Custom error hierarchy for structured error handling.
 * All domain errors should extend AppError — never throw plain Error for domain logic.
 */

export class AppError extends Error {
    readonly code: string
    readonly context: Record<string, unknown>
    override readonly cause?: Error

    constructor(
        message: string,
        code: string,
        context: Record<string, unknown> = {},
        cause?: Error,
    ) {
        super(message)
        this.name = this.constructor.name
        this.code = code
        this.context = context
        this.cause = cause
    }

    toLog(): Record<string, unknown> {
        return {
            error: this.name,
            code: this.code,
            message: this.message,
            context: this.context,
            stack: this.stack,
            cause: this.cause?.message,
        }
    }
}

/** Thrown when input data fails validation (form input, API params, config). */
export class ValidationError extends AppError {
    constructor(message: string, context?: Record<string, unknown>) {
        super(message, 'VALIDATION_ERROR', context)
    }
}

/** Thrown when an external service call fails (Supabase, Intervals.icu, Stripe). */
export class ExternalServiceError extends AppError {
    constructor(service: string, message: string, cause?: Error) {
        super(message, 'EXTERNAL_SERVICE_ERROR', { service }, cause)
    }
}

/** Thrown when internal data invariants are violated. */
export class DataIntegrityError extends AppError {
    constructor(message: string, context?: Record<string, unknown>) {
        super(message, 'DATA_INTEGRITY_ERROR', context)
    }
}

/** Thrown when a training science calculation produces invalid results. */
export class CalculationError extends AppError {
    constructor(message: string, context?: Record<string, unknown>) {
        super(message, 'CALCULATION_ERROR', context)
    }
}
